import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { 
  School, 
  Public, 
  TrendingUp, 
  People, 
  EmojiEvents,
  Support,
  Language,
  Business
} from '@mui/icons-material';

const AnimatedCounter = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);

  return (
    <Typography
      ref={ref}
      variant="h2"
      sx={{
        fontWeight: 900,
        background: 'linear-gradient(135deg, #2c5aa0 0%, #ff6b35 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontSize: { xs: '2.5rem', md: '4rem' },
        lineHeight: 1,
      }}
    >
      {count}+
    </Typography>
  );
};

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const stats = [
    {
      icon: <People sx={{ fontSize: '3rem', color: 'primary.main' }} />,
      number: 500,
      label: 'Students Placed',
      description: 'Successfully guided to their dream destinations',
    },
    {
      icon: <School sx={{ fontSize: '3rem', color: 'secondary.main' }} />,
      number: 7,
      label: 'Office Locations',
      description: 'Global presence across multiple countries',
    },
    {
      icon: <TrendingUp sx={{ fontSize: '3rem', color: 'success.main' }} />,
      number: 15,
      label: 'Years Experience',
      description: 'Proven track record in education consulting',
    },
    {
      icon: <EmojiEvents sx={{ fontSize: '3rem', color: 'warning.main' }} />,
      number: 98,
      label: 'Success Rate',
      description: 'High success rate in visa approvals',
    },
  ];

  const values = [
    {
      icon: <Support />,
      title: 'Student-Centric Approach',
      description: 'We prioritize each student\'s unique goals and aspirations, providing personalized guidance throughout their journey.',
    },
    {
      icon: <Business />,
      title: 'Trusted Partnerships',
      description: 'Strong relationships with top universities and institutions worldwide ensure the best opportunities for our students.',
    },
    {
      icon: <TrendingUp />,
      title: 'Proven Track Record',
      description: 'Years of experience and thousands of successful placements demonstrate our commitment to excellence.',
    },
    {
      icon: <School />,
      title: 'Expert Guidance',
      description: 'Our team of experienced counselors provides comprehensive support from application to enrollment.',
    },
  ];

  return (
    <Box sx={{ py: 8, background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                color: '#1e3a5f',
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                lineHeight: 1.2,
              }}
            >
              About{' '}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(135deg, #2c5aa0 0%, #ff6b35 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Future Forwards
              </Box>
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#64748b',
                maxWidth: '800px',
                mx: 'auto',
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
              Empowering dreams through education. We are your trusted partner in making international education accessible and successful.
            </Typography>
          </Box>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Grid container spacing={4} sx={{ mb: 8 }}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(20px)',
                      borderRadius: 4,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: theme.shadows[3],
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.shadows[8],
                        '&::before': {
                          transform: 'scaleX(1)',
                        },
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(135deg, #2c5aa0 0%, #ff6b35 100%)',
                        transform: 'scaleX(0)',
                        transition: 'transform 0.3s ease',
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                      <motion.div
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <Box sx={{ mb: 3 }}>
                          {stat.icon}
                        </Box>
                      </motion.div>

                      <AnimatedCounter end={stat.number} />

                      <Typography
                        variant="h6"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 600,
                          mb: 2,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontSize: '1.1rem',
                        }}
                      >
                        {stat.label}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        {stat.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Mission Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box sx={{ mb: 8 }}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h3"
                  sx={{
                    color: '#1e3a5f',
                    mb: 3,
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    fontWeight: 700,
                  }}
                >
                  Our Mission
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#64748b',
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  At Future Forwards Education, we believe that quality education knows no boundaries. 
                  Our mission is to bridge the gap between ambitious students and world-class educational 
                  opportunities across the globe.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#64748b',
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                  }}
                >
                  We are committed to providing comprehensive guidance, personalized support, and 
                  transparent processes that make studying abroad not just a dream, but a reality 
                  for every student we serve.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #2c5aa0 0%, #ff6b35 100%)',
                      borderRadius: 4,
                      p: 4,
                      color: 'white',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                      }}
                    >
                      "Education is the passport to the future"
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        opacity: 0.9,
                        fontWeight: 400,
                      }}
                    >
                      - Malcolm X
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Box>
        </motion.div>

        {/* Values Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h3"
              sx={{
                color: '#1e3a5f',
                textAlign: 'center',
                mb: 6,
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
              }}
            >
              Our Values
            </Typography>
            <Grid container spacing={4}>
              {values.map((value, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Card
                      sx={{
                        p: 3,
                        height: '100%',
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(44, 90, 160, 0.1)',
                        borderRadius: 3,
                        boxShadow: theme.shadows[2],
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: theme.shadows[4],
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 50,
                            height: 50,
                            background: 'linear-gradient(135deg, #2c5aa0, #ff6b35)',
                            color: 'white',
                            flexShrink: 0,
                          }}
                        >
                          {value.icon}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              color: '#1e3a5f',
                              fontWeight: 600,
                              mb: 1,
                            }}
                          >
                            {value.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#64748b',
                              lineHeight: 1.6,
                            }}
                          >
                            {value.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default About;
